
import { EventSlot } from "../../events/EventSlot";
import { Arrays } from "../../tools/Arrays";
import UndoManager from "../../undo/UndoManager";
import { RegExpUtility } from "../RegExpUtility";
import { Caret, CaretPlacement } from "./Caret";
import { Line } from "./Line";
import { LinesSplitter } from "./LinesSplitter";
import { TextEditorEvent } from "./TextEditorEvent";


export class TextEditor
{
  protected _lines:Line[] = [];
  get lines()
  {
    return this._lines;
  }

  protected _selectionStart = new Caret( 0, 0, CaretPlacement.BEFORE );
  protected _selectionEnd   = new Caret( 0, 0, CaretPlacement.BEFORE );
  protected _splitter = new LinesSplitter();

  readonly onChange = new EventSlot<TextEditorEvent>();
  
  get selectedLineIndexMin()
  {
    return Math.min( this._selectionStart.line , this._selectionEnd.line );
  }

  get selectedLineIndexMax()
  {
    return Math.max( this._selectionStart.line , this._selectionEnd.line );
  }

  get numSelectedLines()
  {
    return ( this.selectedLineIndexMax - this.selectedLineIndexMin ) + 1;
  }

  get startLine()
  {
    return this._lines[ this._selectionStart.line ];
  }

  get endLine()
  {
    return this._lines[ this._selectionEnd.line ];
  }

  get selectedLineIndex()
  {
    return this._selectionEnd.line;
  }

  get selectedLine()
  {
    return this.endLine;
  }

  get upperSelection()
  {
    if ( this._selectionStart.line < this._selectionEnd.line )
    {
      return this._selectionStart;
    }

    return this._selectionEnd;
  }

  get upperSelectedLine()
  {
    return this._lines[ this.upperSelection.line ];
  }

  get lowerSelection()
  {
    if ( this._selectionStart.line < this._selectionEnd.line )
    {
      return this._selectionEnd;
    }

    return this._selectionStart;
  }

  get lowerSelectedLine()
  {
    return this._lines[ this.lowerSelection.line ];
  }

  get mostLeftSelection()
  {
    if ( this._selectionStart.line !== this._selectionEnd.line )
    {
      return this.upperSelection;
    }

    return this._selectionStart.characterIndex < this._selectionEnd.characterIndex ? this._selectionStart : this._selectionEnd;

  }

 
  get selectionCharacterIndex()
  {
    return this._selectionEnd.characterIndex;
  }

  protected _undo = new UndoManager();

  constructor()
  {
    this.onChange.addListener(
      ()=>
      {
        console.log( "LINES:", this._lines );
      }
    )

    console.log( "SPLITTING SPACE", new LinesSplitter().split( " " ) );
    console.log( "SPLITTING TEXT", new LinesSplitter().split( " Hello Whats\nUp? " ) );
  }

  setSelection( start:Caret, end:Caret = undefined )
  { 
    this._selectionStart = start.clone();
    this._selectionEnd = ( end || start ).clone();    
  }

  get text()
  {
    let lines = this._lines.map( l => l.text );

    return lines.join( "" );
  }

  get hasNoSelection()
  {
    return Caret.equals( this._selectionStart, this._selectionEnd );
  }

  get hasSelection()
  {
    return ! this.hasNoSelection;
  }

  get selectionInfo()
  {
    let lowerInfo = `${ ( this.lowerSelection.line ) }:${ this.lowerSelection.characterIndex }`;
    let upperInfo = `${ ( this.upperSelection.line ) }:${ this.upperSelection.characterIndex }`;

    if ( this._selectionStart.line === this._selectionEnd.line )
    {
      if ( this._selectionStart.character === this._selectionEnd.character )
      {
        return `${ lowerInfo }|`;
      }
      else
      {
        let lineInfo = `${ ( this.lowerSelection.line ) }:`;
        let lowerCharacterInfo = `${ this.lowerSelection.characterIndex }`;
        let upperCharacterInfo = `${ this.upperSelection.characterIndex }`;

        if ( this._selectionEnd === this.lowerSelection )
        {
          return `${lineInfo}( [${ lowerCharacterInfo }] - ${ upperCharacterInfo } )`;
        }
        else
        {
          return `${lineInfo}( ${ lowerCharacterInfo } - [${ upperCharacterInfo }] )`;
        }

      }
    }
    else
    {      
      if ( this._selectionEnd === this.lowerSelection )
      {
        return `( [${ lowerInfo }] - ${ upperInfo } )`;
      }
      else
      {
        return `( ${ lowerInfo } - [${ upperInfo }] )`;
      }

      
    }
  }

  clearSelection()
  {
    console.log( "clearSelection", `current text: "${this.text}"`, this.selectionInfo )  

    if ( Arrays.isEmpty( this._lines ) || this.hasNoSelection )
    {
      console.log( "clearSelection done", `current text: "${this.text}"`, this.selectionInfo )
      return;
    }

    if ( this.numSelectedLines === 1 )
    {
      console.log( "Cutout one line");      
      
      this.selectedLine.cutout( this._selectionStart.characterIndex, this._selectionEnd.characterIndex );

      this.setSelection( this.mostLeftSelection );
    }
    else
    {      

      let upperLine = this.upperSelectedLine;
      let lowerLine = this.lowerSelectedLine;

      console.log( "Cutout multiple lines", upperLine, lowerLine );   

      upperLine.cutout( this.upperSelection.characterIndex, upperLine.content.length );
      lowerLine.cutout( 0, this.lowerSelection.characterIndex );

      upperLine.content += lowerLine.content;

      let removeStart  = this.selectedLineIndexMin + 1;
      let removeNumber = this.selectedLineIndexMax - this.selectedLineIndexMin;
      
      this._lines.splice( removeStart, removeNumber );

      this.setSelection( this.mostLeftSelection );
    }

    console.log( "clearSelection done", `current text: "${this.text}"`, this.selectionInfo )
    this.onChange.dispatch( new TextEditorEvent() );
  }

  addText( text:string )
  {
    this.clearSelection();

    console.log( "addText", `current text: "${this.text}"`, this.selectionInfo )

    let splitLines = this._splitter.split( text );

    if ( Arrays.isEmpty( this._lines ) )
    {
      this._lines = splitLines.map( sl => Line.from( sl ) );
      let lastLine = Arrays.getLast( this._lines );

      this.setSelection( Caret.after( this._lines.length - 1, lastLine.content.length - 1 ) );
      this.onChange.dispatch( new TextEditorEvent() );

      console.log( "addText done", `current text: "${this.text}"`, this.selectionInfo )
      return;
    }

    let before = this.selectedLine.getContentBefore( this.selectionCharacterIndex );
    let after  = this.selectedLine.getContentAfter( this.selectionCharacterIndex );

    console.log( "Selected line split:", `"${before}" "${after}"` );

    this.selectedLine.content = before + splitLines[ 0 ].content;

    this.setSelection( Caret.after( this.selectedLineIndex, this.selectedLine.content.length - 1 ) );
    
    let insertIndex = this.selectedLineIndex + 1;

    let lastLine = this.selectedLine;
    
    for ( let i = 1; i < splitLines.length; i++ )
    {
      let line = new Line();
      
      line.content = splitLines[ i ].content;
      line.setBreakType( splitLines[ i ].breakType );

      Arrays.insert( this._lines, line, insertIndex );

      insertIndex ++;

      lastLine = line;

    }

    let currentLength = lastLine.content.length;
    lastLine.content += after;

    this.setSelection( Caret.after( this.lines.indexOf( lastLine ), currentLength - 1 ) );

    console.log( "addText done", `current text: "${this.text}"`, this.selectionInfo )
    this.onChange.dispatch( new TextEditorEvent() );

  }


  removePrevious()
  {
    console.log( "removePrevious", `current text: "${this.text}"`, this.selectionInfo )

    if ( this._selectionStart.line === 0 && this._selectionStart.characterIndex === 0 )
    {
      console.log( "nothing to remove" );
      return;
    }

    if ( this.hasNoSelection )
    {
      console.log( "nothing selected" );

      if (  this._selectionStart.characterIndex === 0 )
      {
        console.log( "wrap to line before" );
        let lineBefore = this._lines[ this._selectionStart.line - 1 ];
        this._selectionEnd = new Caret( this._selectionEnd.line - 1, lineBefore.content.length - 1, CaretPlacement.BEFORE );
      }
      else 
      {
        console.log( "select previous" );
        this._selectionEnd = new Caret( this._selectionEnd.line, this._selectionEnd.character - 1, this._selectionEnd.placement );
      }
    }
    else
    {
      console.log( "Something selected, clearing selection" );
    }

    this.clearSelection();

    console.log( "removePrevious", `current text: "${this.text}"`, this.selectionInfo )
  }

  removeNext()
  {

  }
}